import { Button } from "antd";
import Logo from "assets/img/logo.png";
import slide1 from "assets/onboarding/slide1.png";
import slide2 from "assets/onboarding/slide2.png";
import slide3 from "assets/onboarding/slide3.png";
import slide4 from "assets/onboarding/slide4.png";
import slide5 from "assets/onboarding/slide5.png";
import { FadeIn, ShimmerCta } from "components/polish";
import { useOnboarding } from "hooks/useOnboarding";
import type React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	trackOnboardingOverviewCompleted,
	trackOnboardingOverviewShown,
	trackOnboardingOverviewSkipped,
} from "utils/activationAnalytics";
import { trackEvents } from "utils/analytics";

const slides = [
	{
		title: "Welcome to Equility",
		text: "To get started, we’ll give you a quick overview of how Equility can improve your accounting accuracy and efficiency.",
		image: slide1,
		demoId: "cmkfq4ix01dpxw7p2i5ea0coh",
	},
	{
		title: "Easily reconcile any of your general ledger accounts",
		text: "Simply connect to your bank account and we’ll flag any transactions with QuickBooks Online that don’t match your bank statement in seconds.",
		image: slide2,
		demoId: "cmkdc63kl0csxqm44a4ud0zbo",
	},
	{
		title: "Complete your financial review in a fraction of the time",
		text: "It has never been easier to close your books accurately and on time. Simply answer a few questions about your business, and receive an on-demand assessment of your accounting accuracy.",
		image: slide3,
		demoId: "cmkgei8rb07pl1363imcbg5nx",
	},
	{
		title: "Create recurring projects and tasks to keep your team organized",
		text: "Easily create custom projects and tasks for each of clients, so that you can stay on track of your progress and never missed a deadline.",
		image: slide4,
		demoId: "cmkeh02kb02w8w7p2tms5j0ni",
	},
	{
		title: "Convert your PDF bank statements",
		text: "Use our file converter to easily turn your PDF bank statements into downloadable spreadsheet data.",
		image: slide5,
		demoId: "cmkczogg407c5sqwdbvukqsuu",
	},
];

export function OnBoardOverview() {
	const [current, setCurrent] = useState(0);
	const navigate = useNavigate();
	const { complete } = useOnboarding(false);

	// Rendered in place from Protected.tsx — no route change, so no $pageview.
	// 🪤 Keep this effect's `[]` separate from the per-slide one below. Folding
	// the two together would fan `onboarding_overview_shown` out to one fire per
	// slide instead of one per mount.
	useEffect(() => {
		trackOnboardingOverviewShown();
	}, []);

	// One event per slide the user actually lands on, including the first. Keyed
	// on `current` rather than fired from the click handlers so the dots and the
	// arrow keys are counted too — they are three ways to reach the same slide.
	useEffect(() => {
		trackEvents.onboarding.stepViewed(
			current,
			slides[current].title,
			slides.length,
		);
	}, [current]);

	const finish = async (completedVia: "finished" | "skipped") => {
		await complete();
		// Both events below are emitted after the write, so a failed PATCH does
		// not record a gate the user never actually passed.
		//
		// Two names, two questions, on purpose:
		// - `onboarding_completed` (EQU-18) — the user left onboarding by either
		//   path, discriminated by `completedVia`.
		// - `onboarding_overview_completed` (EQU-37) — the user walked the whole
		//   tour. A skip is its own step there, so it stays conditional.
		trackEvents.onboarding.completed(completedVia, slides.length);
		if (completedVia === "finished") trackOnboardingOverviewCompleted();
		// Land on All Companies, the app's home route.
		//
		// It is the list a signed-in user works from: every client, its
		// connection status and last review, with the team filter they left set.
		// The firm close board stays one click away in the menu.
		// `openAddCompany` is kept in state so a firm with no clients still gets
		// the create prompt it needs: All Companies opens its Add Company drawer
		// on that flag (the firm board never read it).
		navigate("/", { state: { openAddCompany: true } });
	};

	const nextSlide = async () => {
		if (current === slides.length - 1) {
			await finish("finished");
		} else {
			setCurrent((prev) => prev + 1);
		}
	};
	const prevSlide = () => setCurrent((prev) => prev - 1);
	const skip = async () => {
		// Both before the write, and the first carries the slide they bailed from —
		// that index is the whole point of the event, and `finish` navigates away.
		trackEvents.onboarding.skipped(current, slides.length);
		trackOnboardingOverviewSkipped();
		await finish("skipped");
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "ArrowRight") nextSlide();
		else if (e.key === "ArrowLeft" && current > 0) prevSlide();
	};

	return (
		<div className="intro-container" onKeyDown={handleKeyDown} tabIndex={0}>
			<style>{`
        .circle-slice {
          position: absolute;
          top: -70px;
          left: 0;
          width: clamp(100px, 25vw, 280px);   /* scaled relative to logo */
          height: clamp(100px, 25vw, 280px);
          background: var(--color-primary);
          opacity: 0.15;
          border-radius: 0 0 100% 0; /* bottom-right quarter */
          z-index: 0; /* behind header/logo */
        }
        .intro-container {
          position: relative;
          min-height: 100vh;
          padding: 5% 10%;
          box-sizing: border-box;
          font-family: inherit;
        }
        .intro-header {
          text-align: left;
          margin-bottom: 4rem;
          position: relative;
          z-index: 1;
        }
        .logo {
          max-width: 20%;   
          height: auto;
        }
        .intro-main {
          display: flex;
          flex-wrap: wrap;
          gap: 5%;
          align-items: center;
          z-index: 1;
          position: relative;
        }
        .intro-left {
          flex: 1 1 45%;
        }
        .intro-left h1 {
          font-size: 2rem;
          margin-bottom: 2rem;
        }
        .intro-left p {
          font-size: 1.125rem;
          margin-bottom: 2rem;
        }
        .next-btn {
          background: var(--color-primary);
          color: white;
          font-size: 1.125rem;
          padding: 12px 24px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
        }
        .skip-text {
          margin-top: 1rem;
          color: gray;
          text-decoration: underline;
          cursor: pointer;
        }
        .dots {
          margin-top: 1.5rem;
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .dot {
          width: 12px;
          height: 12px;
          background: #bbb;
          border-radius: 50%;
          display: inline-block;
          cursor: pointer;
        }
        .dot.active {
          background: var(--color-primary);
        }
        .intro-right {
          flex: 1 1 45%;
          text-align: center;
        }
        .slide-wrapper {
          position: relative;
          width: 100%;
          border-radius: 8px;
          overflow: hidden;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .slide-wrapper:hover {
          transform: scale(1.01);
          box-shadow: 0 8px 24px rgba(0,0,0,0.15);
        }
        .slide-wrapper img {
          width: 100%;
          height: auto;
          display: block;
        }
        .slide-play-btn {
          position: absolute;
          bottom: 16px;
          right: 16px;
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(0,0,0,0.55);
          color: white;
          padding: 8px 14px;
          border-radius: 20px;
          font-size: 0.875rem;
          font-weight: 500;
          pointer-events: none;
          backdrop-filter: blur(4px);
          transition: background 0.15s ease;
        }
        .slide-play-btn svg {
          flex-shrink: 0;
        }
        .slide-wrapper:hover .slide-play-btn {
          background: rgba(15, 150, 72, 0.85);
        }
        @media (max-width: 768px) {
          .intro-main {
            flex-direction: column;
          }
          .intro-left, .intro-right {
            flex: 1 1 100%;
          }
          .logo {
            max-width: 70%;
          }
        }
      `}</style>

			<div className="circle-slice"></div>

			<header className="intro-header">
				<img src={Logo} alt="Equility Logo" className="logo" />
			</header>

			<main className="intro-main">
				<div className="intro-left">
					{/* key={current} remounts on slide change so the reveal replays. */}
					<FadeIn key={current}>
						<h1>{slides[current].title}</h1>
						<p>{slides[current].text}</p>
					</FadeIn>

					<div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
						{current > 0 && <Button onClick={prevSlide}>← Back</Button>}
						{current === slides.length - 1 ? (
							<ShimmerCta onClick={nextSlide}>Get Started →</ShimmerCta>
						) : (
							<Button type="primary" onClick={nextSlide}>
								Next →
							</Button>
						)}
					</div>

					{current < slides.length - 1 && (
						<p className="skip-text" onClick={skip}>
							Skip our quick overview
						</p>
					)}

					<div
						style={{
							marginTop: "1rem",
							fontSize: "0.875rem",
							color: "var(--color-text-muted)",
						}}
					>
						Step {current + 1} of {slides.length}
					</div>
					<div className="dots" role="tablist" aria-label="Onboarding slides">
						{slides.map((slide, idx) => (
							<span
								key={idx}
								role="tab"
								aria-selected={idx === current}
								aria-label={`Go to slide ${idx + 1}: ${slide.title}`}
								className={`dot ${idx === current ? "active" : ""}`}
								onClick={() => setCurrent(idx)}
							></span>
						))}
					</div>
				</div>

				<div className="intro-right">
					<div
						className="slide-wrapper"
						onClick={() =>
							(window as any).Supademo?.open(slides[current].demoId)
						}
						style={{ cursor: "pointer" }}
					>
						<img src={slides[current].image} alt={slides[current].title} />
						<div className="slide-play-btn">
							<svg width="14" height="14" viewBox="0 0 14 14" fill="white">
								<polygon points="2,1 13,7 2,13" />
							</svg>
							Watch demo
						</div>
					</div>
				</div>
			</main>
		</div>
	);
}
